import { useEffect, useState } from "react";
import { apiFetch } from "@/lib/api";

type Stats = {
  totalLeads: number;
  novosHoje: number;
  novosSemana: number;
  agendados: number;
  compareceu: number;
  convertido: number;
  noShow: number;
  conversao: number;
  attendance: number;
  serie: Array<{ d: string; n: number }>;
  proximos: Array<{
    id: number;
    data: string;
    dia_semana: string;
    inicio: string;
    fim: string;
    capacidade: number;
    usados: number;
  }>;
};

const DOW: Record<string, string> = {
  domingo: "Dom",
  segunda: "Seg",
  terca: "Ter",
  quarta: "Qua",
  quinta: "Qui",
  sexta: "Sex",
  sabado: "Sáb",
};

function fmtDate(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

function fmtTime(t: string): string {
  const [hh, mm] = t.split(":");
  if (!hh) return t;
  const h = Number(hh);
  return mm && mm !== "00" ? `${h}h${mm}` : `${h}h`;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<Stats>("/admin/stats", { auth: "admin" })
      .then(setStats)
      .catch(() => setError("Não conseguimos carregar os dados."));
  }, []);

  if (error) return <div className="adm-login-error">{error}</div>;
  if (!stats) return <p style={{ color: "var(--ink-3)" }}>Carregando…</p>;

  const maxSerie = Math.max(1, ...stats.serie.map((s) => s.n));

  return (
    <>
      <div className="adm-topbar">
        <div>
          <h1 className="adm-title">Visão geral</h1>
          <p className="adm-sub">Resumo do funil e do calendário.</p>
        </div>
      </div>

      <section className="adm-cards">
        <Card label="Total de leads" value={stats.totalLeads} />
        <Card label="Novos hoje" value={stats.novosHoje} />
        <Card label="Agendados" value={stats.agendados} />
        <Card label="Conversão" value={`${stats.conversao}%`} sub={`${stats.convertido} convertidos`} />
      </section>

      <section className="adm-panel">
        <div className="adm-panel-head">
          <h2 className="adm-panel-title">Leads por dia (últimos 30)</h2>
          <span className="adm-sub">{stats.novosSemana} nos últimos 7 dias</span>
        </div>
        <div className="adm-spark">
          {stats.serie.length === 0 && (
            <p style={{ color: "var(--ink-3)", fontSize: 14 }}>Sem dados.</p>
          )}
          {stats.serie.map((s) => (
            <div
              key={s.d}
              className="adm-spark-bar"
              style={{ height: `${(s.n / maxSerie) * 100}%` }}
              title={`${s.d}: ${s.n}`}
            />
          ))}
        </div>
      </section>

      <section className="adm-panel" style={{ marginTop: 22 }}>
        <div className="adm-panel-head">
          <h2 className="adm-panel-title">Próximos horários com agendamento</h2>
        </div>
        {stats.proximos.length === 0 ? (
          <p style={{ color: "var(--ink-3)", fontSize: 14 }}>
            Nenhum horário com agendamento confirmado.
          </p>
        ) : (
          <table className="adm-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Dia</th>
                <th>Horário</th>
                <th>Ocupação</th>
              </tr>
            </thead>
            <tbody>
              {stats.proximos.map((p) => (
                <tr key={p.id}>
                  <td data-label="Data">{fmtDate(p.data)}</td>
                  <td data-label="Dia">{DOW[p.dia_semana] ?? p.dia_semana}</td>
                  <td data-label="Horário">{fmtTime(p.inicio)} às {fmtTime(p.fim)}</td>
                  <td data-label="Ocupação">{p.usados} / {p.capacidade}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

function Card({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="adm-card">
      <span className="adm-card-label">{label}</span>
      <div className="adm-card-value">{value}</div>
      {sub && <span className="adm-card-delta">{sub}</span>}
    </div>
  );
}
